import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Inject,
  Renderer2,
  PLATFORM_ID,
  Optional,
  ChangeDetectorRef,
  HostListener,
  ElementRef
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil, tap, catchError, map } from 'rxjs/operators';
import { AosToken, aos } from '../../shared/aos';

import { LangService } from 'src/app/services/lang.service';
import { Lang } from 'src/app/models/lang';
import { Strings } from '../../const/strings';
import { CarService } from 'src/app/services/car.service';
import { HeadLinkService } from 'src/app/services/head-link.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { CarListItem, CarCollection } from 'src/app/models/car';
import { ServerError } from '../../../Exceptions/ServerError';
import { SortRequest } from '../../requests/sort-request';
import { BrandService } from '../../services/brand.service';
import { Brand } from '../../models/brand';
import { metaAttrList } from 'src/app/const/meta-attribute-list';
import { CurrentQuery } from '../../models/current-query';
import { SsrTransferStateService } from 'src/app/services/ssr-transfer-state.service';

@Component({
  selector: 'app-car-list',
  templateUrl: './car-list.component.html',
  styleUrls: ['./car-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarListComponent implements OnInit, OnDestroy {

  carCollection$: Observable<CarCollection>;
  brands$: Observable<Brand[]>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  appUrl: string;
  currentPath = '/cars';
  currentRouterLinkPath = '/';
  currentQueries: CurrentQuery[] = [];
  private currentPageNumber = 1;
  lang: Lang;
  strings = Strings;
  skeletonItemNumbers = Array(24);
  isSortSelling: boolean;
  sortBrandId = '';
  sortBrandName = '';
  brandCollection: Brand[] = [];
  private pageYOffset = 0;

  constructor(
    @Optional() @Inject(RESPONSE) private response: Response,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private titleService: Title,
    private elementRef: ElementRef,
    private langService: LangService,
    private metaService: Meta,
    private carService: CarService,
    private ssrTransferState: SsrTransferStateService,
    private brandService: BrandService,
    private headLinkService: HeadLinkService,
    private googleAnalyticsService: TrackingService,
    public changeDetectorRef: ChangeDetectorRef,
    @Inject(AosToken) aos: any,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  ngOnInit() {
    this.appUrl = this.langService.getAppUrl();
    this.lang = this.langService.getCurrentLang();
    this.setBodyClassName();
    this.setup();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.setLastListPageOffsetY();
  }

  getCarLabelClassName(car: CarListItem): string {
    if (car.sold) {
      return 'soldout';
    } else if (car.inTransaction) {
      return 'in_transaction';
    } else if (car.previousPrice) {
      return 'discount';
    } else if (car.new) {
      return 'newcar';
    }
    return '';
  }

  @HostListener('window:scroll', [ '$event' ])
  onWindowScroll(event: Event) {
    const scrollYKey = 'scrollY';
    this.pageYOffset = event.currentTarget[scrollYKey];
  }

  private setBodyClassName() {
    if (typeof document === 'undefined') {
      return;
    }
    this.renderer.addClass(document.body, 'stocklist_page_body');
    this.renderer.addClass(document.body, 'secondlevel_page_body');
    if (this.lang === 'en') {
      this.renderer.addClass(document.body, 'lang_en');
    }
  }

  private setTitle() {
    this.titleService.setTitle(this.metaTitle);
  }

  private setCurrentPath() {
    let routerLinkPath = '/';
    let path = '/cars';
    if (this.sortBrandId) {
      routerLinkPath += 'makes/' + this.sortBrandId;
      path += '/makes/' + this.sortBrandId;
    }
    this.currentPath = path;
    this.currentRouterLinkPath = routerLinkPath;
  }

  private setCurrentQuery() {
    const query: CurrentQuery[] = [];
    if (this.isSortSelling) {
      query.push({
        key: 'selling',
        value: '1',
      });
    }
    if (this.currentPageNumber > 1) {
      query.push({
        key: 'page',
        value: this.currentPageNumber.toString(),
      });
    }
    this.currentQueries = query;
  }

  get currentQueryString() {
    if (this.currentQueries.length === 0) {
      return '';
    }
    const queries = this.currentQueries.map(currentQuery => currentQuery.key + '=' + currentQuery.value);
    return '?' + queries.join('&');
  }

  private removeExistingMetaTag() {
    metaAttrList.forEach(attr => {
      this.metaService.removeTag(attr);
    });
  }

  private setHeadLink() {
    const url = this.langService.getAppUrl() + this.currentPath + this.currentQueryString;
    const lang = this.langService.getCurrentLang();
    this.headLinkService.setCanonicalURL(url);
    this.headLinkService.setAlternate(url, lang);
  }

  private sendGoogleAnalytics() {
    const lang = this.langService.getCurrentLang();
    const path = `/${lang}${this.currentPath}${this.currentQueryString}`;
    this.googleAnalyticsService.sendPageView(path, this.metaTitle);
  }

  private addMeta() {
    this.metaService.addTags([
      { name: 'description', content: this.metaDescription },
    ]);
  }

  get metaTitle() {
    const brandName = this.brandName ? this.brandName + ' - ' : '';
    const prefix = this.isSortSelling
      ? this.strings.carList.sellingPrefix[this.lang]
      : this.strings.carList.publishedPrefix[this.lang];
    const pageNumber = this.currentPageNumber > 1
      ? ` (Page ${this.currentPageNumber})`
      : '';
    return this.strings.carList.title[this.lang]
      .replace(/\$1/, brandName)
      .replace(/\$2/, prefix)
      .replace(/\$3/, pageNumber);
  }

  get metaDescription() {
    const brandName = this.brandName ? this.brandName +  this.strings.carList.brandSuffix[this.lang] : '';
    const label = this.isSortSelling
      ? this.strings.carList.sellingDescriptionPrefix[this.lang]
      : this.strings.carList.publishDescriptionPrefix[this.lang];
    const pageNumber = this.currentPageNumber > 1
      ? `(Page ${this.currentPageNumber})`
      : '';
    return this.strings.carList.metaDescription[this.lang]
      .replace(/\$1/, brandName)
      .replace(/\$2/, label)
      .replace(/\$3/, pageNumber);
  }

  get pageTitle() {
    const brandName = this.brandName;
    if (brandName !== '') {
      return this.strings.carList.pageBrandTitle[this.lang].replace(/%s/, brandName);
    }
    return this.strings.carList.pageTitle[this.lang];
  }

  private get brandName() {
    if (this.brandCollection.length > 0 && this.sortBrandId) {
      const filteredBrands = this.brandCollection.filter(brand => brand.id === this.sortBrandId);
      if (filteredBrands.length > 0) {
        return filteredBrands[0].name;
      }
    }
    return '';
  }

  private setup() {
    combineLatest([this.route.params, this.route.queryParams]).pipe(
      tap(([pathParams, queryParams]) => {
        this.isSortSelling = queryParams.selling === '1';
        this.sortBrandId = pathParams.brandId || '';
        this.currentPageNumber = queryParams.page ? queryParams.page : '';
        this.setCurrentPath();
        this.setCurrentQuery();
        this.removeExistingMetaTag();
        this.setHeadLink();
        const sortRequest: SortRequest = {
          selling: this.isSortSelling,
          brandId: this.sortBrandId,
          page: this.currentPageNumber.toString(),
        };
        this.fetchBrandList();
        this.fetchCarList(sortRequest);
      })
    ).subscribe();
  }

  private fetchCarList(sortRequest: SortRequest) {
    this.carCollection$ = this.ssrTransferState.get(
      this.elementRef.nativeElement.tagName + '-CARS',
      this.carService.fetchAll(this.lang, sortRequest)
    )
      .pipe(
        takeUntil(this.destroy$),
        tap(carCollection => {
          // tslint:disable-next-line: no-string-literal
          if (carCollection['status'] !== undefined && carCollection['status'] === 500) {
            // 500 Internal Server Error
            throw new ServerError('Internal Server Error');
          }
          if (carCollection.items.length === 0) {
            this.router.navigate(['/not-found']);
            return;
          }
          this.scrollToLastListPageOffsetY();
          this.setupAOS();
        }),
        catchError(error => {
          if (isPlatformServer(this.platformId) && this.response) {
            this.response.status(503);
          }
          if (isPlatformBrowser(this.platformId)) {
            this.snackBar.open(this.strings.ServerError.ServiceUnavailable[this.lang]);
          }
          throw new ServerError(error);
        })
      );
  }

  private fetchBrandList() {
    this.brands$ = this.ssrTransferState.get(
      this.elementRef.nativeElement.tagName + '-BRANDS',
      this.brandService.fetchAll(this.lang)
    )
      .pipe(
        takeUntil(this.destroy$),
        map(brands => {
          return brands.sort(this.sortBrands.bind(this));
        }),
        tap(brands => {
          this.invokeBrand(brands);
        }),
        catchError(error => {
          if (isPlatformServer(this.platformId) && this.response) {
            this.response.status(503);
          }
          if (isPlatformBrowser(this.platformId)) {
            this.snackBar.open(this.strings.ServerError.ServiceUnavailable[this.lang]);
          }
          throw new ServerError(error);
        })
      );
  }

  private invokeBrand(brands: Brand[]) {
    this.brandCollection = brands;
    this.setSortedBrandName();
    this.setTitle();
    this.addMeta();
    this.sendGoogleAnalytics();
    this.changeDetectorRef.detectChanges();
  }

  private setSortedBrandName() {
    if (!this.sortBrandId || this.brandCollection.length === 0) {
      this.sortBrandName = '';
      return;
    }
    const filteredBrands = this.brandCollection.filter((brand) => brand.id === this.sortBrandId);
    if (filteredBrands.length > 0) {
      this.sortBrandName = filteredBrands[0].name;
      return;
    }
    this.sortBrandName = '';
  }

  private sortBrands(a: Brand, b: Brand) {
    // priorityが大きい場合は前へ
    if (a.priority < b.priority) { return 1; }
    // priorityが小さい場合は後ろへ
    if (a.priority > b.priority) { return -1; }
    if (this.isSortSelling) {
      // carSellingCountが大きい場合は前へ
      if (a.carSellingCount < b.carSellingCount) { return 1; }
      // carSellingCountが小さい場合は後ろへ
      if (a.carSellingCount > b.carSellingCount) { return -1; }
    } else {
      // carTotalCountが大きい場合は前へ
      if (a.carTotalCount < b.carTotalCount) { return 1; }
      // carTotalCountが小さい場合は後ろへ
      if (a.carTotalCount > b.carTotalCount) { return -1; }
    }
    return 0;
  }

  private setLastListPageOffsetY() {
    if (typeof window === 'undefined') {
      return;
    }
    this.carService.lastListPageOffsetY = this.pageYOffset;
    const pathQueryString = this.currentPath + this.currentQueryString;
    this.carService.lastListPagePath = pathQueryString;
  }

  private scrollToLastListPageOffsetY() {
    if (typeof window === 'undefined') {
      return;
    }
    const pathQueryString = this.currentPath + this.currentQueryString;
    if (this.carService.lastListPageOffsetY && this.carService.lastListPagePath === pathQueryString) {
      setTimeout(() => {
        window.scrollTo(0, this.carService.lastListPageOffsetY);
      }, 200);
    }
  }

  private setupAOS() {
    if (typeof document === 'undefined') {
      return;
    }
    aos.init({
      duration: 1200,
      delay: 200,
      once: true,
      mirror: true,
    });
  }

  trackById(index: number, car: CarListItem) {
    return car.id;
  }

}
